import { gql, useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { Header, Modal, Table } from '../../components';
import { Search } from '../../components/Header/components';
import { Block, Button, SelectInput, Text, TextInput } from '../../components/layout';
import ButtonAction from '../../components/Table/components/ButtonAction';
import { SvgIconEdit, SvgIconModalDelete, SvgIconTrash } from '../../svgs';
import { store } from '../../utils/store';
import validateFormSchema from '../../utils/validateFormSchema';

function CaseStudies() {
    const { language } = useContext(store);
    const history = useHistory();
    const paginate = useRef(false);
    const [search, setSearch] = useState("");
    const [modalCreateIsOpen, setModalCreateIsOpen] = useState(false);
    const [modalDeleteIsOpen, setModalDeleteIsOpen] = useState(null);

    const [result, setResult] = useState({ edges: [], pageInfo: { hasNextPage: false }, totalCount: 0 });
    const [templates, setTemplates] = useState([]);

    const [queryCaseStudies, { loading }] = useLazyQuery(
        gql`
            query ($first: Int, $after: String, $search: String) {
                caseStudies(first: $first, after: $after, search: $search) {
                    edges {
                        node {
                            id
                            title
                            slug
                        }
                        cursor
                    }
                    pageInfo {
                        startCursor
                        endCursor
                        hasNextPage
                    }
                    totalCount
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.caseStudies) {
                    setResult({
                        edges: paginate.current ? [...result.edges, ...data.caseStudies.edges] : data.caseStudies.edges,
                        pageInfo: data.caseStudies.pageInfo,
                        totalCount: data.caseStudies.totalCount
                    });
                    paginate.current = false;
                }
            }
        }
    );

    const [mutationCaseStudyCreate, { loading: loadingCaseStudyCreate }] = useMutation(
        gql`
            mutation caseStudyCreate($slug: String!, $template: ID!) {
                caseStudyCreate(slug: $slug, template: $template) {
                    id
                    title
                    slug
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.caseStudyCreate) {
                    history.push(`/editor/case-study/${data.caseStudyCreate.id}`);
                }
            }
        }
    );

    const [mutationCaseStudyDelete, { loading: loadingCaseStudyDelete }] = useMutation(
        gql`
            mutation caseStudyDelete($id: ID!) {
                caseStudyDelete(id: $id) {
                    id
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.caseStudyDelete) {
                    const index = result.edges.findIndex((edge) => edge.cursor == data.caseStudyDelete.id);
                    if (index !== -1) {
                        result.edges.splice(index, 1);
                        setModalDeleteIsOpen(null);
                        setResult([...data]);
                    }
                }
            }
        }
    );

    const [queryTemplates, { loading: loadingTemplates }] = useLazyQuery(
        gql`
            query {
                caseStudiesTemplates {
                    id
                    name
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.caseStudiesTemplates) {
                    setTemplates(data.caseStudiesTemplates.map((template) => ({ value: template.id, label: template.name })));
                }
            }
        }
    );

    const requestData = (params = { next: false }) => {
        const { first, after, next } = params;

        if (next) {
            paginate.current = true;
        }

        queryCaseStudies({
            variables: {
                first: first || 20,
                after: after || undefined,
                search: search !== "" ? search : undefined
            }
        });
    }

    useEffect(() => {
        requestData();
        queryTemplates();
    }, [language]);

    useEffect(() => {
        requestData();
    }, [search]);

    return (
        <>
            <Block flex height="100%">
                <Header
                    leftChildren={<Search onChange={(e) => setSearch(e.target.value)} />}
                    rightChildren={<Button style={{ minWidth: 309 }} text="Add Case Study" onClick={() => setModalCreateIsOpen(true)} />}
                />
                <Block height="100%" mr={20} ml={20} style={{ overflow: "hidden" }}>
                    <Table
                        loading={loading}
                        columns={[
                            { label: "Title", key: "title" },
                            { label: "Slug", key: "slug" }
                        ]}
                        rows={result.edges.map((edge) => ({ ...edge.node, actions: <><ButtonAction icon={<SvgIconEdit />} text="Edit" onClick={() => history.push(`/editor/case-study/${edge.cursor}`)} /><ButtonAction icon={<SvgIconTrash />} onClick={() => setModalDeleteIsOpen(edge.cursor)} /></> }))}
                        next={() => requestData({ next: true, after: result.pageInfo.endCursor })}
                        hasMore={result.pageInfo.hasNextPage}
                    />
                </Block>
            </Block>
            <Modal
                title="Create CaseStudy"
                isOpen={modalCreateIsOpen}
                onRequestClose={() => setModalCreateIsOpen(false)}
            >
                <Text size={14} height={20}>CaseStudy</Text>
                <Block mt={28}>
                    <Formik
                        initialValues={{}}
                        validateOnChange={false}
                        validateOnBlur={false}
                        validate={values => {
                            return validateFormSchema(
                                yup.object().shape({
                                    slug: yup.string().required(),
                                    template: yup.number().required()
                                }),
                                values
                            )
                        }}
                        onSubmit={(values, { setSubmitting }) => {
                            setSubmitting(false);
                            mutationCaseStudyCreate({ variables: values });
                        }}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit} autoComplete="off">
                                    <Block>
                                        <Block>
                                            <Block>
                                                <TextInput name="slug" label="Slug" type="text" onChange={handleChange} onBlur={handleBlur} error={errors.slug} />
                                            </Block>
                                            <Block mt={12}>
                                                <SelectInput name="template" label="Template" type="text" onBlur={handleBlur} error={errors.template} options={templates} />
                                            </Block>
                                        </Block>
                                        <Block mt={44}>
                                            <Button type="submit" text="Submit" loading={loadingCaseStudyCreate} />
                                        </Block>
                                    </Block>
                                </form>
                            )
                        }}
                    </Formik>
                </Block>
            </Modal>
            <Modal
                width={280}
                title="Delete Case Study"
                isOpen={!!modalDeleteIsOpen}
                onRequestClose={() => setModalDeleteIsOpen(null)}
            >
                <Block mb={20}>
                    <SvgIconModalDelete />
                </Block>
                <Text size={18} height={24}>Are you sure that you want to delete this case study?</Text>
                <Text size={12} height={14} mt={8}>This action it’s irreversible, it will erase all the data from this case study.</Text>
                <Block mt={32}>
                    <Button text="Delete" loading={loadingCaseStudyDelete} onClick={() => mutationCaseStudyDelete({ variables: { id: modalDeleteIsOpen } })} />
                </Block>
            </Modal>
        </>
    );
}

export default CaseStudies;